import { ErrorBoundary } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { PAGE_GUTTER } from "@/components/page";
import {
  APPS_KEY,
  type AppSnapshotInfo,
  fetchMaandrapportJob,
  MAANDRAPPORT_SNAPSHOT_KEY,
  startMaandrapportRecompute,
  useApps,
  useBezettingSection,
  useBezettingSnapshot,
  useMaandrapportSnapshot,
} from "@/lib/api";
import { apiErrorCode, apiErrorMessage, isToastedError } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { AppAccessDenied } from "../access-denied";
import {
  AppEmpty,
  Banner,
  BANNER_MAX,
  type BannerItem,
  isAppDenied,
  isHandledRendererError,
  rendererErrorText,
} from "../bezetting/host";
import { RecomputeController } from "../bezetting/recompute";
import { RecomputeDialog } from "../bezetting/recompute-dialog";
import { snapshotRef } from "../bezetting/snapshot-sections";
import { bezettingFigure } from "./bezetting-figure";
import {
  type BezettingTile,
  MAANDRAPPORT_SCHEMA,
  type MaandrapportTab,
  type RecomputeHook,
} from "./contract";
import { maandrapportJobErrorText } from "./job-errors";
import {
  createMaandrapportDetailLoader,
  createMaandrapportSectionLoader,
  createMaandrapportViewLoader,
} from "./sections";

// The renderer and d3 load with the app's first visit, not with the dashboard.
const MaandrapportMount = lazy(() =>
  import("./mount").then((module) => ({ default: module.MaandrapportMount })),
);

/** The slug of Bezettingsgraad in the apps list: Overzicht links to it only for whoever may open it. */
const BEZETTING_SLUG = "bezetting";

interface MaandrapportHostProps {
  tab: MaandrapportTab;
  /** The client whose page is open, under Klanten. */
  client?: number | null;
}

/**
 * The Maandrapport page, hosted as Bezettingsgraad is: its title, a banner for what went wrong, and the renderer,
 * which draws the app's strip (the Odoo-stand, Herbereken), its tabs and the page. Before the first recompute the
 * page offers Herbereken itself; to someone who may not open the app it says so, and who to ask. Herbereken's
 * progress shows in a dialog over the page.
 */
export function MaandrapportHost({ tab, client = null }: MaandrapportHostProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const summary = useMaandrapportSnapshot();
  const [banner, setBanner] = useState<BannerItem[]>([]);
  const [dismissedJob, setDismissedJob] = useState<string | null>(null);
  // The snapshot a view or detail called outdated (409 snapshot_outdated): Herbereken in its place until a new one.
  const [outdatedId, setOutdatedId] = useState<string | null>(null);
  const onOutdated = useCallback(
    (id: string) => {
      logger.debug("[maandrapport]", "outdated snapshot", id);
      setOutdatedId(id);
      // The summary names the recompute the backend started for it, which the button then follows.
      void queryClient.invalidateQueries({ queryKey: MAANDRAPPORT_SNAPSHOT_KEY, exact: true });
    },
    [queryClient],
  );
  const report = useCallback((text: () => string) => {
    setBanner((items) => [
      ...items.slice(1 - BANNER_MAX),
      { id: Date.now() + Math.random(), text },
    ]);
  }, []);
  const [recompute] = useState(
    () =>
      new RecomputeController({
        queryClient,
        start: startMaandrapportRecompute,
        fetchJob: fetchMaandrapportJob,
        summaryKey: MAANDRAPPORT_SNAPSHOT_KEY,
        // No meldingen: a new snapshot changes only the apps' list (its badges).
        staleOnComplete: [APPS_KEY],
        logTag: "[maandrapport]",
        onFailure: (error) => {
          if (!isToastedError(error)) {
            report(() => apiErrorMessage(error, "recompute"));
          }
        },
      }),
  );
  useEffect(() => {
    recompute.attach();
    return () => recompute.detach();
  }, [recompute]);

  // Access taken away while the page was open: the sidebar drops the app too.
  const denied = isAppDenied(summary.error);
  useEffect(() => {
    if (denied) {
      void queryClient.invalidateQueries({ queryKey: APPS_KEY, exact: true });
    }
  }, [denied, queryClient]);

  const title = t("apps.maandrapport.title");
  const data = summary.data;
  const failedJob =
    data?.job?.status === "failed" && data.job.id !== dismissedJob ? data.job : null;

  let content;
  if (denied) {
    logger.debug("[maandrapport]", "no access");
    content = <AppAccessDenied />;
  } else if (data) {
    const snapshot = data.snapshot;
    if (!snapshot) {
      content = <AppEmpty recompute={recompute} body={t("apps.maandrapport.emptyBody")} />;
    } else if (snapshot.schema_version !== MAANDRAPPORT_SCHEMA || snapshot.id === outdatedId) {
      // A snapshot of the first version holds none of the facts a range is computed from.
      content = (
        <AppEmpty
          recompute={recompute}
          title={t("apps.maandrapport.outdatedTitle")}
          body={t("apps.maandrapport.outdatedBody")}
        />
      );
    } else {
      content = (
        <MaandrapportView
          info={snapshot}
          tab={tab}
          client={client}
          recompute={recompute}
          report={report}
          onOutdated={onOutdated}
        />
      );
    }
  } else if (summary.isError) {
    content = (
      <LoadErrorCard
        message={t("apps.loadError", { app: title })}
        onRetry={() => void summary.refetch()}
      />
    );
  } else {
    content = <LoadingCard />;
  }

  return (
    <div className={cn("mx-auto w-full max-w-[1440px] py-8", PAGE_GUTTER)}>
      <h1 className="font-heading text-primary mb-6 text-2xl font-bold">{title}</h1>
      <Banner
        items={[
          ...(failedJob && !denied
            ? [
                {
                  id: -1,
                  text: () => maandrapportJobErrorText(failedJob, Boolean(data?.snapshot)),
                  onDismiss: () => setDismissedJob(failedJob.id),
                },
              ]
            : []),
          ...banner.map((item) => ({
            ...item,
            onDismiss: () => setBanner((items) => items.filter((other) => other.id !== item.id)),
          })),
        ]}
      />
      {content}
      {!denied && (
        <RecomputeDialog
          recompute={recompute}
          summary={data}
          errorText={maandrapportJobErrorText}
        />
      )}
    </div>
  );
}

interface MaandrapportViewProps {
  info: AppSnapshotInfo;
  tab: MaandrapportTab;
  client: number | null;
  recompute: RecomputeHook;
  report: (text: () => string) => void;
  /** The backend called this snapshot outdated, or its meta did. */
  onOutdated: (snapshotId: string) => void;
}

/**
 * Bezettingsgraad's key figure for the Overzicht's tile, from that app's latest snapshot and its own Overzicht
 * section: only for whoever may open it (null otherwise), the figure null while it loads or without a snapshot.
 */
function useBezettingTile(): BezettingTile | null {
  const apps = useApps();
  const allowed = Boolean(apps.data?.apps.some((app) => app.slug === BEZETTING_SLUG));
  const summary = useBezettingSnapshot({ enabled: allowed });
  const snapshotId = summary.data?.snapshot?.id ?? null;
  const listed = Boolean(summary.data?.snapshot?.sections.some((s) => s.key === "overzicht"));
  const section = useBezettingSection(snapshotId, "overzicht", { enabled: allowed && listed });
  const figure = useMemo(() => bezettingFigure(section.data ?? null), [section.data]);
  return useMemo(() => (allowed ? { figure } : null), [allowed, figure]);
}

/** The renderer on the latest snapshot; a newer snapshot takes over in place. */
function MaandrapportView({
  info,
  tab,
  client,
  recompute,
  report,
  onOutdated,
}: MaandrapportViewProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const bezetting = useBezettingTile();
  const snapshot = useMemo(() => snapshotRef(info), [info]);
  const refetchSummary = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: MAANDRAPPORT_SNAPSHOT_KEY, exact: true }),
    [queryClient],
  );
  const outdated = useCallback(() => onOutdated(snapshot.id), [onOutdated, snapshot.id]);
  const loaders = useMemo(() => {
    const options = {
      // A pruned snapshot: the summary names the newest, which this view then shows.
      onSnapshotGone: refetchSummary,
      // Access taken away: the summary answers 403 now, and the host shows the denied page.
      onDenied: refetchSummary,
      // A snapshot of an older schema: the host asks for Herbereken in its place.
      onOutdated: outdated,
    };
    return {
      sections: createMaandrapportSectionLoader(queryClient, snapshot, options),
      view: createMaandrapportViewLoader(queryClient, snapshot, options),
      detail: createMaandrapportDetailLoader(queryClient, snapshot, options),
    };
  }, [queryClient, snapshot, refetchSummary, outdated]);
  const onError = useCallback(
    (error: unknown) => {
      if (isHandledRendererError(error) || apiErrorCode(error) === "snapshot_outdated") {
        return;
      }
      logger.error(
        "[maandrapport]",
        "renderer",
        error instanceof Error ? error.name : typeof error,
      );
      report(() => rendererErrorText(error));
    },
    [report],
  );
  return (
    // A renderer that fails to load (its file replaced by a release) or to draw stays inside the page.
    <ErrorBoundary
      fallback={
        <LoadErrorCard
          message={t("apps.loadError", { app: t("apps.maandrapport.title") })}
          onRetry={() => window.location.reload()}
        />
      }
    >
      <Suspense fallback={<LoadingCard />}>
        <MaandrapportMount
          tab={tab}
          client={client}
          search={search.replace(/^\?/, "")}
          snapshot={snapshot}
          sections={loaders.sections}
          view={loaders.view}
          detail={loaders.detail}
          recompute={recompute}
          bezetting={bezetting}
          onError={onError}
          onOutdated={outdated}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
